import styled, { css, DefaultTheme, StyledComponent } from 'styled-components';
import { IContainer, IItem, TDirection, TSpacing, IGridStyled } from './types';

export const Grid = styled.div<IGridStyled>`
  ${(props) => itemMixin(props)};
  ${({ theme, container, ...props }) =>
    container && containerMixin(props, theme)};
` as StyledComponent<'div', DefaultTheme, IGridStyled, never>;

function isColumn(direction: TDirection) {
  return direction.startsWith('column');
}

function axisPadding(
  direction: TDirection,
  spacing: TSpacing,
  theme: DefaultTheme,
  resetOppositeAxis = false
) {
  const gap = calcSpacing(spacing, theme);

  return isColumn(direction)
    ? css`
        & > ${Grid} {
          padding-top: ${gap};
          padding-bottom: ${gap};
          ${resetOppositeAxis &&
          css`
            padding-left: 0;
            padding-right: 0;
          `}
        }

        & > ${Grid}:first-child {
          padding-top: 0;
        }

        & > ${Grid}:last-child {
          padding-bottom: 0;
        }
      `
    : css`
        & > ${Grid} {
          padding-left: ${gap};
          padding-right: ${gap};
          ${resetOppositeAxis &&
          css`
            padding-top: 0;
            padding-bottom: 0;
          `}
        }

        & > ${Grid}:first-child {
          padding-left: 0;
        }

        & > ${Grid}:last-child {
          padding-right: 0;
        }
      `;
}

function containerMixin(
  {
    direction = 'row',
    directionSm,
    justify = 'start',
    align = 'stretch',
    spacing = 1,
  }: IContainer,
  theme: DefaultTheme
) {
  return css`
    display: flex;
    flex-direction: ${direction};
    justify-content: ${justify};
    align-items: ${align};

    ${axisPadding(direction, spacing, theme)}

    ${!!directionSm &&
    directionSm !== direction &&
    css`
      @media (max-width: ${theme.breakpoint.sm.maxWidth}) {
        flex-direction: ${directionSm};

        & > ${Grid} {
          flex: initial;
        }

        ${axisPadding(directionSm, spacing, theme, true)}
      }
    `}
  `;
}

function itemMixin({ xs, $fill }: IItem) {
  return css`
    ${xs &&
    css`
      flex: ${(100 / 12) * xs}% 0 0;
    `}

    ${$fill &&
    css`
      flex: 1;
    `}
  `;
}

function calcSpacing(spacing: TSpacing, theme: DefaultTheme) {
  const mappedSpacing = theme.spacing[`km${spacing}`];

  return `calc(${mappedSpacing}/2)`;
}
